import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@munifocus/ui';
import {
  APPEAL_DECISIONS,
  APPELLANT_ROLES,
  decisionNeedsComplianceDate,
  type AppealDecision,
  type AppealDto,
  type AppealVariation,
  type AppellantRole,
} from '@munifocus/contracts-gov';
import {
  useCommitteeCandidates,
  useDecideAppeal,
  useRecordAppeal,
  useRecordHearing,
  useRejectAppeal,
  useScheduleHearing,
} from '../../lib/appeals-api';
import { todayIso } from '../../lib/cost-recovery-display';

/** `yyyy-mm-ddThh:mm` in the browser's zone, for a datetime-local input. */
function localNow(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

/** A datetime-local value, read in the browser's zone, as an ISO instant. */
function toIso(local: string): string {
  return new Date(local).toISOString();
}

interface DialogProps {
  fileId: string;
  open: boolean;
  onClose: () => void;
}

// ── Record an appeal received by letter, email or at the counter ──────────

export function RecordAppealDialog({ fileId, open, onClose }: DialogProps) {
  const { t } = useTranslation();
  const record = useRecordAppeal(fileId);
  const [name, setName] = useState('');
  const [role, setRole] = useState<AppellantRole>('owner');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [locale, setLocale] = useState<'en' | 'fr'>('en');
  const [filedAt, setFiledAt] = useState(localNow);
  const [grounds, setGrounds] = useState('');
  const [relief, setRelief] = useState('');
  const valid = name.trim() && grounds.trim() && filedAt;

  const submit = () =>
    record.mutate(
      {
        appellantName: name.trim(),
        appellantRole: role,
        appellantEmail: email.trim() || undefined,
        appellantPhone: phone.trim() || undefined,
        appellantLocale: locale,
        grounds: grounds.trim(),
        reliefSought: relief.trim() || undefined,
        filedAt: toIso(filedAt),
      },
      { onSuccess: onClose },
    );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.appeals.record.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('gov.appeals.fields.appellantName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>{t('gov.appeals.fields.appellantRole')}</InputLabel>
            <Select
              label={t('gov.appeals.fields.appellantRole')}
              value={role}
              onChange={(e) => setRole(e.target.value as AppellantRole)}
            >
              {APPELLANT_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.appeals.role.${r}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('gov.appeals.fields.email')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText={t('gov.appeals.fields.emailHint')}
            fullWidth
          />
          <TextField
            label={t('gov.appeals.fields.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>{t('gov.appeals.fields.language')}</InputLabel>
            <Select
              label={t('gov.appeals.fields.language')}
              value={locale}
              onChange={(e) => setLocale(e.target.value as 'en' | 'fr')}
            >
              <MenuItem value="en">{t('gov.appeals.language.en')}</MenuItem>
              <MenuItem value="fr">{t('gov.appeals.language.fr')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('gov.appeals.fields.filedAt')}
            type="datetime-local"
            value={filedAt}
            onChange={(e) => setFiledAt(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            fullWidth
          />
          <TextField
            label={t('gov.appeals.fields.grounds')}
            value={grounds}
            onChange={(e) => setGrounds(e.target.value)}
            multiline
            minRows={3}
            required
            fullWidth
          />
          <TextField
            label={t('gov.appeals.fields.reliefSought')}
            value={relief}
            onChange={(e) => setRelief(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || record.isPending}>
          {t('gov.appeals.record.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Reject an owner's appeal ──────────────────────────────────────────────

export function RejectAppealDialog({
  fileId,
  appeal,
  onClose,
}: {
  fileId: string;
  appeal: AppealDto | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const reject = useRejectAppeal(fileId);
  const [reason, setReason] = useState('');
  return (
    <Dialog open={Boolean(appeal)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.appeals.reject.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">{t('gov.appeals.reject.hint')}</Alert>
          <TextField
            label={t('gov.appeals.reject.reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={3}
            required
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!reason.trim() || reject.isPending}
          onClick={() =>
            appeal &&
            reject.mutate(
              { appealId: appeal.id, reason: reason.trim() },
              {
                onSuccess: () => {
                  setReason('');
                  onClose();
                },
              },
            )
          }
        >
          {t('gov.appeals.reject.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Schedule a hearing ────────────────────────────────────────────────────

export function ScheduleHearingDialog({
  fileId,
  appeal,
  onClose,
}: {
  fileId: string;
  appeal: AppealDto | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const candidates = useCommitteeCandidates(fileId, Boolean(appeal));
  const schedule = useScheduleHearing(fileId);
  const [when, setWhen] = useState('');
  const [location, setLocation] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [chair, setChair] = useState('');
  const [notify, setNotify] = useState(true);
  const fr = i18n.language.startsWith('fr');
  const valid = when && location.trim() && members.length > 0 && members.includes(chair);

  const toggle = (id: string) =>
    setMembers((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  return (
    <Dialog open={Boolean(appeal)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.appeals.schedule.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('gov.appeals.schedule.when')}
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            fullWidth
          />
          <TextField
            label={t('gov.appeals.schedule.location')}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
            fullWidth
          />
          <FormControl component="fieldset">
            <FormLabel component="legend">{t('gov.appeals.schedule.members')}</FormLabel>
            {candidates.data?.length === 0 && (
              <Alert severity="warning">{t('gov.appeals.schedule.noOfficials')}</Alert>
            )}
            {candidates.data?.map((c) => {
              const title = fr ? c.titleFr || c.titleEn : c.titleEn || c.titleFr;
              return (
                <FormControlLabel
                  key={c.id}
                  control={
                    <Checkbox checked={members.includes(c.id)} onChange={() => toggle(c.id)} />
                  }
                  label={title ? `${c.name}, ${title}` : c.name}
                />
              );
            })}
          </FormControl>
          <FormControl fullWidth disabled={members.length === 0}>
            <InputLabel>{t('gov.appeals.schedule.chair')}</InputLabel>
            <Select
              label={t('gov.appeals.schedule.chair')}
              value={members.includes(chair) ? chair : ''}
              onChange={(e) => setChair(e.target.value)}
            >
              {candidates.data
                ?.filter((c) => members.includes(c.id))
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
            label={
              appeal?.appellantEmail
                ? t('gov.appeals.schedule.notify', { email: appeal.appellantEmail })
                : t('gov.appeals.schedule.noEmail')
            }
            disabled={!appeal?.appellantEmail}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || schedule.isPending}
          onClick={() =>
            appeal &&
            schedule.mutate(
              {
                appealId: appeal.id,
                scheduledFor: toIso(when),
                location: location.trim(),
                memberIds: members,
                chairId: chair,
                notifyAppellant: notify,
              },
              { onSuccess: onClose },
            )
          }
        >
          {t('gov.appeals.schedule.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Record what happened at the hearing ───────────────────────────────────

export function RecordHearingDialog({
  fileId,
  target,
  onClose,
}: {
  fileId: string;
  target: { appealId: string; hearingId: string } | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const record = useRecordHearing(fileId);
  const [outcome, setOutcome] = useState<'held' | 'adjourned' | 'cancelled'>('held');
  const [attended, setAttended] = useState(true);
  const [attendees, setAttendees] = useState('');
  const [minutes, setMinutes] = useState('');

  return (
    <Dialog open={Boolean(target)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.appeals.hearing.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{t('gov.appeals.hearing.outcome')}</InputLabel>
            <Select
              label={t('gov.appeals.hearing.outcome')}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as typeof outcome)}
            >
              <MenuItem value="held">{t('gov.appeals.hearingStatus.held')}</MenuItem>
              <MenuItem value="adjourned">{t('gov.appeals.hearingStatus.adjourned')}</MenuItem>
              <MenuItem value="cancelled">{t('gov.appeals.hearingStatus.cancelled')}</MenuItem>
            </Select>
          </FormControl>
          {outcome === 'held' && (
            <FormControlLabel
              control={
                <Checkbox checked={attended} onChange={(e) => setAttended(e.target.checked)} />
              }
              label={t('gov.appeals.hearing.attended')}
            />
          )}
          <TextField
            label={t('gov.appeals.hearing.attendees')}
            value={attendees}
            onChange={(e) => setAttendees(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label={t('gov.appeals.hearing.minutes')}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            helperText={t('gov.appeals.hearing.minutesHint')}
            multiline
            minRows={4}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={record.isPending}
          onClick={() =>
            target &&
            record.mutate(
              {
                ...target,
                outcome,
                appellantAttended: outcome === 'held' ? attended : undefined,
                attendees: attendees.trim() || undefined,
                minutes: minutes.trim() || undefined,
              },
              { onSuccess: onClose },
            )
          }
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── The committee's decision ──────────────────────────────────────────────

export function DecideAppealDialog({
  fileId,
  appeal,
  onClose,
}: {
  fileId: string;
  appeal: AppealDto | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const decide = useDecideAppeal(fileId);
  const [decision, setDecision] = useState<AppealDecision | ''>('');
  const [variation, setVariation] = useState<AppealVariation>('modified');
  const [date, setDate] = useState(() => todayIso());
  const [deadline, setDeadline] = useState('');
  const [reasons, setReasons] = useState('');
  const [notify, setNotify] = useState(true);
  const needsDate = decision !== '' && decisionNeedsComplianceDate(decision);
  const valid = decision !== '' && date && reasons.trim() && (!needsDate || deadline);

  return (
    <Dialog open={Boolean(appeal)} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('gov.appeals.decide.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel>{t('gov.appeals.decide.decision')}</InputLabel>
            <Select
              label={t('gov.appeals.decide.decision')}
              value={decision}
              onChange={(e) => setDecision(e.target.value as AppealDecision)}
            >
              {APPEAL_DECISIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {t(`gov.appeals.decision.${d}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {needsDate && (
            <FormControl fullWidth>
              <InputLabel>{t('gov.appeals.decide.variation')}</InputLabel>
              <Select
                label={t('gov.appeals.decide.variation')}
                value={variation}
                onChange={(e) => setVariation(e.target.value as AppealVariation)}
              >
                <MenuItem value="modified">{t('gov.appeals.variation.modified')}</MenuItem>
                <MenuItem value="extended">{t('gov.appeals.variation.extended')}</MenuItem>
              </Select>
            </FormControl>
          )}
          <TextField
            label={t('gov.appeals.decide.date')}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            fullWidth
          />
          {needsDate && (
            <TextField
              label={t('gov.appeals.decide.deadline')}
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: date } }}
              required
              fullWidth
            />
          )}
          <TextField
            label={t('gov.appeals.decide.reasons')}
            value={reasons}
            onChange={(e) => setReasons(e.target.value)}
            multiline
            minRows={4}
            required
            fullWidth
          />
          {decision === 'quashed' && (
            <Alert severity="warning">{t('gov.appeals.decide.quashedNote')}</Alert>
          )}
          {decision !== '' && decision !== 'quashed' && (
            <Alert severity="info">{t('gov.appeals.decide.kingsBenchNote')}</Alert>
          )}
          <FormControlLabel
            control={<Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
            label={
              appeal?.appellantEmail
                ? t('gov.appeals.decide.notify', { email: appeal.appellantEmail })
                : t('gov.appeals.schedule.noEmail')
            }
            disabled={!appeal?.appellantEmail}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.actions.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || decide.isPending}
          onClick={() =>
            appeal &&
            decision !== '' &&
            decide.mutate(
              {
                appealId: appeal.id,
                decision,
                variation: needsDate ? variation : undefined,
                decisionDate: date,
                revisedComplianceDeadline: needsDate ? deadline : undefined,
                reasons: reasons.trim(),
                notifyAppellant: notify,
              },
              { onSuccess: onClose },
            )
          }
        >
          {t('gov.appeals.decide.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
